import { useCallback, useContext, useMemo, useState } from 'react'

import {
  SanityPrintSlotSettings,
  SanityPrintType,
} from '@data/sanity/queries/types/print'
import { LanguageContext } from '@lib/language'
import { PrintShopFormContext } from '@lib/print/print-shop-form-context'
import { PrintShopFormStringsContext } from '@lib/print/print-shop-form-strings-context'
import { removeShopPrint } from '@lib/print/request'
import { ShopPrint } from '@lib/print/types'

import Button from '@components/buttons/button'
import ShopFormPrintItem from '../print-item'
import UploadStepUploadArea from './upload-area'

interface UploadStepUploadControlsProps {
  stepIndex: number
  printSlot?: SanityPrintSlotSettings
  prints: ShopPrint[]
}

const UploadStepUploadControls = ({
  stepIndex,
  printSlot,
  prints,
}: UploadStepUploadControlsProps) => {
  const { locale } = useContext(LanguageContext)
  const {
    printPermissions,
    shopState,
    shopFormSteps,
    dispatchShopState,
    setActiveStepIndex,
  } = useContext(PrintShopFormContext)
  const { shopFormStrings } = useContext(PrintShopFormStringsContext)

  const [deletedPrintIds, setDeletedPrintIds] = useState<string[]>([])

  // Get print types based on permissions and slot type
  const printTypes = useMemo(() => {
    if (!printSlot) {
      return []
    }

    const allPrintTypes: SanityPrintType[] = ['logo', 'image']
    return allPrintTypes.filter((printType) => {
      return printPermissions.some((printPermission) => {
        return (
          printPermission.slot.type === printSlot.type &&
          !!printPermission[printType]
        )
      })
    })
  }, [printPermissions, printSlot])

  const handleRemoveClick = useCallback(
    async (printId: string) => {
      if (!shopState.id) {
        return
      }

      // Disable print entry
      setDeletedPrintIds((currentIds) => [...currentIds, printId])

      try {
        await removeShopPrint(locale, shopState.id, printId)

        // Remove print from the list
        dispatchShopState({
          action: 'removePrint',
          id: printId,
        })
      } catch (error) {
        console.error(error)

        // Enable print entry
        setDeletedPrintIds((currentIds) => [
          ...currentIds.filter((currentId) => currentId !== printId),
        ])
      }
    },
    [dispatchShopState, locale, shopState.id],
  )

  if (!printSlot) {
    return null
  }

  const showSkipButton =
    prints.length === 0 && stepIndex < shopFormSteps.length - 1

  return (
    <div className="space-y-4">
      <div className="space-y-8">
        <UploadStepUploadArea
          printSlotType={printSlot.type}
          printTypes={printTypes}
        />

        {prints.length > 0 && (
          <div className="flex flex-col gap-4">
            {prints.map((print) => (
              <ShopFormPrintItem
                key={print.id}
                mode="edit"
                print={print}
                disabled={deletedPrintIds.includes(print.id)}
                onRemove={handleRemoveClick}
              />
            ))}
          </div>
        )}
      </div>

      {showSkipButton && (
        <Button
          variant="secondary"
          size="normal-small"
          color="gray-light"
          onClick={() => {
            setActiveStepIndex((currentStepIndex) => currentStepIndex + 1)
          }}
          className="w-full"
        >
          {shopFormStrings.shopFormSkipUploadButton}
        </Button>
      )}
    </div>
  )
}

export default UploadStepUploadControls
